/**
 * Image Processing Service - WebP Conversion & Optimization
 * Handles automatic conversion to WebP format for storage savings and performance
 */

export interface ImageProcessingOptions {
  quality?: number; // 0.1 to 1.0, default 0.8
  maxWidth?: number; // Max width in pixels
  maxHeight?: number; // Max height in pixels
  format?: 'webp' | 'original' | 'auto'; // Force format, keep original, or auto-detect
  preserveMetadata?: boolean; // Keep EXIF data (default false for privacy)
  preserveGifAnimation?: boolean; // Keep GIF animations (default true)
}

export interface ProcessedImage {
  blob: Blob;
  originalSize: number;
  compressedSize: number;
  compressionRatio: number;
  format: string;
  dimensions: { width: number; height: number };
}

export class ImageProcessingService {
  private static instance: ImageProcessingService;
  
  // Default settings optimized for crypto/NFT content
  private defaultOptions: Required<ImageProcessingOptions> = {
    quality: 0.85, // High quality for NFT images
    maxWidth: 2048, // 2K max for featured images
    maxHeight: 2048,
    format: 'auto', // Auto-detect best format
    preserveMetadata: false,
    preserveGifAnimation: true // Keep GIF animations by default
  };

  public static getInstance(): ImageProcessingService {
    if (!ImageProcessingService.instance) {
      ImageProcessingService.instance = new ImageProcessingService();
    }
    return ImageProcessingService.instance;
  }

  /**
   * Main processing method - converts and optimizes images
   */
  async processImage(
    file: File, 
    options: Partial<ImageProcessingOptions> = {}
  ): Promise<ProcessedImage> {
    const opts = { ...this.defaultOptions, ...options };
    
    // Validate input
    if (!this.isValidImageFile(file)) {
      throw new Error('Invalid image file type');
    }

    // Special handling for GIFs
    if (file.type === 'image/gif') {
      return await this.processGifImage(file, opts);
    }

    // Check if already WebP and meets size requirements
    if (file.type === 'image/webp' && this.meetsRequirements(file, opts)) {
      return {
        blob: file,
        originalSize: file.size,
        compressedSize: file.size,
        compressionRatio: 1,
        format: 'webp',
        dimensions: await this.getImageDimensions(file)
      };
    }

    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas context not available');

      // Load image
      const img = await this.loadImage(file);
      
      // Calculate optimal dimensions
      const dimensions = this.calculateOptimalDimensions(
        img.width, 
        img.height, 
        opts.maxWidth, 
        opts.maxHeight
      );

      // Set canvas size
      canvas.width = dimensions.width;
      canvas.height = dimensions.height;

      // Draw and resize image
      ctx.drawImage(img, 0, 0, dimensions.width, dimensions.height);

      // Determine output format
      const outputFormat = this.determineOptimalFormat(file.type, opts.format);
      
      // Convert with specified quality
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (result) => {
            if (result) resolve(result);
            else reject(new Error('Failed to convert image'));
          },
          outputFormat,
          opts.quality
        );
      });

      return {
        blob,
        originalSize: file.size,
        compressedSize: blob.size,
        compressionRatio: file.size / blob.size,
        format: outputFormat.split('/')[1],
        dimensions
      };

    } catch (error) {
      throw new Error(`Image processing failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Optimized processing for different content types
   */
  async processForArticleImage(file: File): Promise<ProcessedImage> {
    return this.processImage(file, {
      quality: 0.85,
      maxWidth: 1200,
      maxHeight: 800
    });
  }

  async processForProfileImage(file: File): Promise<ProcessedImage> {
    return this.processImage(file, {
      quality: 0.8,
      maxWidth: 400,
      maxHeight: 400
    });
  }

  async processForAdImage(file: File): Promise<ProcessedImage> {
    return this.processImage(file, {
      quality: 0.9, // Higher quality for ads
      maxWidth: 1200,
      maxHeight: 630
    });
  }

  async processForNFTImage(file: File): Promise<ProcessedImage> {
    return this.processImage(file, {
      quality: 0.9, // High quality for NFTs
      maxWidth: 2048,
      maxHeight: 2048
    });
  }

  /**
   * Special GIF processing that preserves animation when needed
   */
  private async processGifImage(file: File, options: Required<ImageProcessingOptions>): Promise<ProcessedImage> {
    try {
      // Check if this is an animated GIF
      const isAnimated = await this.isAnimatedGif(file);
      
      // If it's animated and we want to preserve animation, optimize as GIF
      if (isAnimated && options.preserveGifAnimation) {
        return await this.optimizeGif(file, options);
      }
      
      // If it's static or we don't want to preserve animation, convert to WebP
      if (!isAnimated || !options.preserveGifAnimation) {
        // Process as regular image (will convert to WebP)
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Canvas context not available');

        const img = await this.loadImage(file);
        const dimensions = this.calculateOptimalDimensions(
          img.width, 
          img.height, 
          options.maxWidth, 
          options.maxHeight
        );

        canvas.width = dimensions.width;
        canvas.height = dimensions.height;
        ctx.drawImage(img, 0, 0, dimensions.width, dimensions.height);

        const blob = await new Promise<Blob>((resolve, reject) => {
          canvas.toBlob(
            (result) => {
              if (result) resolve(result);
              else reject(new Error('Failed to convert GIF to WebP'));
            },
            'image/webp',
            options.quality
          );
        });

        return {
          blob,
          originalSize: file.size,
          compressedSize: blob.size,
          compressionRatio: file.size / blob.size,
          format: 'webp',
          dimensions
        };
      }

      // Fallback - return original file
      return {
        blob: file,
        originalSize: file.size,
        compressedSize: file.size,
        compressionRatio: 1,
        format: 'gif',
        dimensions: await this.getImageDimensions(file)
      };
    } catch (error) {
      // Fallback to original file
      return {
        blob: file,
        originalSize: file.size,
        compressedSize: file.size,
        compressionRatio: 1,
        format: 'gif',
        dimensions: await this.getImageDimensions(file)
      };
    }
  }

  /**
   * Optimize GIF files while preserving animation
   */
  private async optimizeGif(file: File, options: Required<ImageProcessingOptions>): Promise<ProcessedImage> {
    // For now, we'll return the original GIF with basic size validation
    // In a production environment, you might use a library like gif.js or similar
    // to actually optimize the GIF frames, reduce colors, etc.
    
    const dimensions = await this.getImageDimensions(file);
    
    // Check if resizing is needed
    const needsResize = dimensions.width > options.maxWidth || dimensions.height > options.maxHeight;
    
    if (needsResize) {
      // For now, we'll just warn and return original
      // GIF frame-by-frame resizing not implemented - using original dimensions
      }
    
    // Basic optimization: return original file
    // GIF optimization not implemented - using original file for best quality
    return {
      blob: file,
      originalSize: file.size,
      compressedSize: file.size,
      compressionRatio: 1,
      format: 'gif',
      dimensions
    };
  }

  /**
   * Check if a GIF file is animated
   */
  private async isAnimatedGif(file: File): Promise<boolean> {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const uint8Array = new Uint8Array(e.target?.result as ArrayBuffer);
          
          // Look for multiple image descriptors in GIF
          // GIF87a or GIF89a header check
          const header = String.fromCharCode(...uint8Array.slice(0, 6));
          if (!header.startsWith('GIF')) {
            resolve(false);
            return;
          }
          
          // Simple check: look for Application Extension (0x21, 0xFF) 
          // which often indicates animation in GIF89a
          let pos = 13; // Skip header and logical screen descriptor
          let imageCount = 0;
          
          while (pos < uint8Array.length - 1) {
            if (uint8Array[pos] === 0x21) { // Extension
              if (uint8Array[pos + 1] === 0xFF) { // Application Extension
                // This often indicates animation
                resolve(true);
                return;
              }
              pos += 2;
              // Skip extension data
              while (pos < uint8Array.length && uint8Array[pos] !== 0x00) {
                const blockSize = uint8Array[pos];
                pos += blockSize + 1;
              }
              pos++; // Skip terminator
            } else if (uint8Array[pos] === 0x2C) { // Image Descriptor
              imageCount++;
              if (imageCount > 1) {
                resolve(true);
                return;
              }
              pos++; // Skip to next
            } else {
              pos++;
            }
          }
          
          resolve(false);
        } catch (error) {
          resolve(false);
        }
      };
      reader.readAsArrayBuffer(file);
    });
  }

  /**
   * Determine optimal output format based on input and preferences
   */
  private determineOptimalFormat(inputType: string, formatPreference: 'webp' | 'original' | 'auto'): string {
    switch (formatPreference) {
      case 'webp':
        return 'image/webp';
      case 'original':
        return inputType;
      case 'auto':
        // Auto-detect best format
        if (inputType === 'image/gif') {
          return 'image/gif'; // Keep GIFs as GIFs for animation
        }
        return 'image/webp'; // Default to WebP for better compression
      default:
        return 'image/webp';
    }
  }

  /**
   * Utility methods
   */
  private isValidImageFile(file: File): boolean {
    const validTypes = [
      'image/jpeg',
      'image/jpg', 
      'image/png',
      'image/webp',
      'image/gif' // Preserves animation when possible
    ];
    return validTypes.includes(file.type);
  }

  private meetsRequirements(file: File, options: Required<ImageProcessingOptions>): boolean {
    // Check if file already meets our requirements
    const maxSize = options.maxWidth * options.maxHeight * 4; // Rough estimate
    return file.size <= maxSize * 0.8; // 80% of theoretical max
  }

  private async loadImage(file: File): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = URL.createObjectURL(file);
    });
  }

  private async getImageDimensions(file: File): Promise<{ width: number; height: number }> {
    const img = await this.loadImage(file);
    return { width: img.width, height: img.height };
  }

  private calculateOptimalDimensions(
    originalWidth: number,
    originalHeight: number,
    maxWidth: number,
    maxHeight: number
  ): { width: number; height: number } {
    // If image is already smaller than max dimensions, keep original
    if (originalWidth <= maxWidth && originalHeight <= maxHeight) {
      return { width: originalWidth, height: originalHeight };
    }

    // Calculate aspect ratio
    const aspectRatio = originalWidth / originalHeight;

    let newWidth = originalWidth;
    let newHeight = originalHeight;

    // Scale down to fit within max dimensions while preserving aspect ratio
    if (originalWidth > maxWidth) {
      newWidth = maxWidth;
      newHeight = newWidth / aspectRatio;
    }

    if (newHeight > maxHeight) {
      newHeight = maxHeight;
      newWidth = newHeight * aspectRatio;
    }

    return {
      width: Math.round(newWidth),
      height: Math.round(newHeight)
    };
  }

  /**
   * Generate filename with appropriate extension
   */
  generateWebPFilename(originalFilename: string): string {
    const nameWithoutExtension = originalFilename.replace(/\.[^/.]+$/, '');
    const timestamp = Date.now();
    return `${timestamp}-${nameWithoutExtension}.webp`;
  }

  /**
   * Generate filename preserving original extension for GIFs
   */
  generateOptimizedFilename(originalFilename: string, outputFormat: string): string {
    const nameWithoutExtension = originalFilename.replace(/\.[^/.]+$/, '');
    const timestamp = Date.now();
    const extension = outputFormat.split('/')[1]; // Extract format from mime type
    return `${timestamp}-${nameWithoutExtension}.${extension}`;
  }

  /**
   * Estimate storage savings
   */
  calculateStorageSavings(originalSize: number, compressedSize: number): {
    savedBytes: number;
    savedPercentage: number;
    savedMB: number;
  } {
    const savedBytes = originalSize - compressedSize;
    const savedPercentage = (savedBytes / originalSize) * 100;
    const savedMB = savedBytes / (1024 * 1024);

    return {
      savedBytes,
      savedPercentage: Math.round(savedPercentage * 100) / 100,
      savedMB: Math.round(savedMB * 100) / 100
    };
  }

  /**
   * Browser support detection
   */
  static supportsWebP(): Promise<boolean> {
    return new Promise((resolve) => {
      const webp = new Image();
      webp.onload = webp.onerror = () => {
        resolve(webp.height === 2);
      };
      webp.src = 'data:image/webp;base64,UklGRjoAAABXRUJQVlA4IC4AAACyAgCdASoCAAIALmk0mk0iIiIiIgBoSygABc6WWgAA/veff/0PP8bA//LwYAAA';
    });
  }
}

// Export singleton instance
export const imageProcessingService = ImageProcessingService.getInstance();


